import { parseISO } from 'date-fns';
import type { SchedulingType } from '@/types';

/**
 * Floating bookings store only the promised civil date as UTC midnight
 * (`YYYY-MM-DDT00:00:00.000Z`). Placement treats that instant as day-scoped;
 * occupancy must not read a clock time out of it (#1965). Create and edit
 * write this encoding — never the browser's local midnight.
 */
export function floatingScheduledAtIso(civilDate: string): string {
  const day = civilDate.split('T')[0] ?? civilDate;
  return `${day}T00:00:00.000Z`;
}

/** The two instants the create request carries, as staff-api compares them. */
export interface RequestScheduleTimes {
  scheduledAt: string | null;
  customerRequestedPickupAt: string | null;
}

/**
 * Turn the wizard's `yyyy-MM-dd'T'HH:mm` fields into the request's ISO
 * instants. The submit body and the pickup check both read this, so the form
 * refuses exactly what the server would (#4199).
 *
 * A floating pickup equal to the midnight day marker (e.g. an older draft) is
 * not a real pickup and is not sent (#2117, #4095).
 */
export function requestScheduleTimes(input: {
  schedulingType: SchedulingType;
  scheduledAtStr: string;
  pickupAtStr: string;
}): RequestScheduleTimes {
  const { schedulingType, scheduledAtStr, pickupAtStr } = input;
  const scheduledAt = scheduledAtStr
    ? schedulingType === 'floating'
      ? floatingScheduledAtIso(scheduledAtStr)
      : toIso(scheduledAtStr)
    : null;
  const customerRequestedPickupAt =
    pickupAtStr && !(schedulingType === 'floating' && pickupAtStr === scheduledAtStr)
      ? toIso(pickupAtStr)
      : null;
  return { scheduledAt, customerRequestedPickupAt };
}

/**
 * True when the request would carry a pickup earlier than its scheduledAt —
 * staff-api refuses that with 400 `customerRequestedPickupAt cannot be before
 * scheduledAt` (#4199). Same string comparison as the server's refine; both
 * sides are `toISOString()` output, so string order is time order.
 */
export function pickupBeforeScheduled(times: RequestScheduleTimes): boolean {
  if (!times.scheduledAt || !times.customerRequestedPickupAt) return false;
  return times.customerRequestedPickupAt < times.scheduledAt;
}

/**
 * True for the server's refusal of a pickup before scheduledAt. The 400 body
 * has no machine code, only Zod `details` with the field path (#4199).
 */
export function isPickupBeforeScheduledRefusal(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const { status, data } = error as Error & { status?: unknown; data?: unknown };
  if (status !== 400 || typeof data !== 'object' || data === null) return false;
  const details = (data as { details?: unknown }).details;
  if (!Array.isArray(details)) return false;
  return details.some(
    (issue: unknown) =>
      typeof issue === 'object' &&
      issue !== null &&
      Array.isArray((issue as { path?: unknown }).path) &&
      (issue as { path: unknown[] }).path.includes('customerRequestedPickupAt'),
  );
}

function toIso(value: string): string | null {
  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
